// Formats a kroner amount as a localized DKK currency string. Whole amounts render without
// decimals; amounts carrying øre (e.g. a plan's completion reimbursement tariff) render with
// exactly two, so an amount is never silently rounded to a different number than is stored.
export function formatDkk(amount: number, language: string): string {
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: 'DKK',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(amount);
}

// The bare DKK unit label for the active language ("kr." in Danish, "DKK" in English), for inputs
// that render the editable number themselves and only need the unit beside it.
export function getDkkSymbol(language: string): string {
  const parts = new Intl.NumberFormat(language, {
    style: 'currency',
    currency: 'DKK'
  }).formatToParts(0);
  return parts.find((part) => part.type === 'currency')?.value ?? 'DKK';
}

// Stands in for an amount that is not a number. Intl renders NaN as "NaN kr.",
// which reads as a figure; a dash reads as the absence of one.
const NO_AMOUNT = '—';

// Compact kroner for dense readouts such as the overview KPI cards, where a
// full amount would dominate the card and exact øre carry no meaning at a
// glance ("12,4 mio. kr." in Danish, "DKK 12.4M" in English). Intl handles the
// locale's own compact notation, so this never hardcodes a "M" suffix.
//
// Non-finite input is shown as a dash rather than formatted: these figures are
// summed and divided upstream, and "NaN kr." on a card claims to be a reading.
export function formatDkkCompact(amount: number, language: string): string {
  if (!Number.isFinite(amount)) return NO_AMOUNT;
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency: 'DKK',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(amount);
}
